import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "./primitives";

const inline = (t) => {
  const parts = String(t).split(/(`[^`]+`|\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("`") && p.endsWith("`")) return <code key={i} className="font-mono bg-muted px-0.5">{p.slice(1, -1)}</code>;
    if (p.startsWith("**") && p.endsWith("**")) return <b key={i}>{p.slice(2, -2)}</b>;
    return p;
  });
};

/** Renderer Markdown minimale (titoli, tabelle, liste, paragrafi): nessuna dipendenza aggiuntiva. */
export const MarkdownLite = ({ text }) => {
  const lines = String(text || "").split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const l = lines[i];
    if (l.startsWith("|")) {
      const tbl = [];
      while (i < lines.length && lines[i].startsWith("|")) { tbl.push(lines[i]); i += 1; }
      const rows = tbl.filter((r) => !/^\|\s*-{2,}/.test(r)).map((r) => r.replace(/^\||\|$/g, "").split("|").map((c) => c.trim()));
      out.push(
        <div key={`t${i}`} className="table-scroll my-2"><table className="w-full text-[10px] border border-border">
          <thead><tr className="bg-muted">{rows[0]?.map((c, k) => <th key={k} className="px-2 py-1 text-left border-b border-border">{inline(c)}</th>)}</tr></thead>
          <tbody>{rows.slice(1).map((r, k) => <tr key={k} className="border-t border-border/60">{r.map((c, j) => <td key={j} className="px-2 py-1 align-top">{inline(c)}</td>)}</tr>)}</tbody>
        </table></div>);
      continue;
    }
    if (/^- /.test(l) || /^\d+\. /.test(l)) {
      const items = [];
      while (i < lines.length && (/^- /.test(lines[i]) || /^\d+\. /.test(lines[i]))) { items.push(lines[i].replace(/^(- |\d+\. )/, "")); i += 1; }
      out.push(<ul key={`l${i}`} className="list-disc pl-5 my-1 space-y-0.5">{items.map((it, k) => <li key={k}>{inline(it)}</li>)}</ul>);
      continue;
    }
    const h = l.match(/^(#{1,4})\s+(.*)/);
    if (h) {
      const lvl = h[1].length;
      const cls = lvl === 1 ? "text-base font-black mt-3" : lvl === 2 ? "text-sm font-black mt-3 border-b border-border pb-0.5" : "text-xs font-bold mt-2";
      out.push(<div key={`h${i}`} className={cls}>{inline(h[2])}</div>);
      i += 1; continue;
    }
    if (l.trim() === "") { i += 1; continue; }
    out.push(<p key={`p${i}`} className="my-1 leading-relaxed">{inline(l)}</p>);
    i += 1;
  }
  return <div className="text-[11px]">{out}</div>;
};

/** Report unico del collaudo, letto dal file Markdown servito dall'API (sola lettura). */
export const ReportSection = () => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open || d) return;
    api.get("/collaudo-lettore/report").then(({ data }) => setD(data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail, { status: e.response?.status })));
  }, [open, d]);
  return (
    <Card title="Report unico del collaudo (docs/COLLAUDO_LETTORE_REPORT.md)" subtitle="Testo integrale generato dagli artefatti; stesso contenuto del file consegnato." testId="cl-report">
      <button className="link-tech font-bold hover:underline" onClick={() => setOpen((v) => !v)} data-testid="cl-report-toggle">{open ? "Nascondi il report" : "Mostra il report completo"}</button>
      {open && err && <div className="text-risk-800" data-testid="cl-report-error">{err}</div>}
      {open && !d && !err && <div className="text-muted-foreground">Lettura del report…</div>}
      {open && d && <div className="border border-border p-3 bg-white max-h-[70vh] overflow-auto" data-testid="cl-report-body"><MarkdownLite text={d.markdown} /></div>}
    </Card>
  );
};
